import { useState } from "react";
import { Link } from "react-router-dom";
import { all_routes as route } from "../../router/all_routes";
import Breadcrumb from "../../../core/common/Breadcrumb/breadcrumb";
import {
  FaQuestionCircle,
  FaChevronDown,
  FaChevronUp,
  FaEnvelope,
  FaGraduationCap,
  FaLanguage,
  FaFileAlt,
} from "react-icons/fa";
import { useTranslation } from "react-i18next";

const categoryIcons = [
  <FaGraduationCap className="text-blue-500" />,
  <FaLanguage className="text-emerald-500" />,
  <FaFileAlt className="text-violet-500" />,
  <FaFileAlt className="text-amber-500" />,
  <FaFileAlt className="text-amber-500" />,
  <FaQuestionCircle className="text-[#009fe3]" />,
];

const categoryColors = [
  "bg-blue-50 border-blue-200",
  "bg-emerald-50 border-emerald-200",
  "bg-violet-50 border-violet-200",
  "bg-amber-50 border-amber-200",
  "bg-amber-50 border-amber-200",
  "bg-gray-50 border-gray-200",
];

const FaqG2G = () => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const { t } = useTranslation();

  const questions = t("faq.questions", { returnObjects: true }) as { q: string; a: string }[];

  return (
    <>
      <Breadcrumb title={t("faq.title")} />

      <section className="bg-gradient-to-br from-gray-50 to-blue-50 py-16 font-sans min-h-screen">
        <div className="max-w-6xl mx-auto px-6">
          {/* Page Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-[#009fe3] to-cyan-500 rounded-2xl mb-6 shadow-lg">
              <FaQuestionCircle className="text-3xl text-white" />
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4 tracking-tight">
              {t("faq.title")}
            </h2>
            <div className="mx-auto w-24 h-1 bg-gradient-to-r from-[#009fe3] to-cyan-500 rounded-full mb-6"></div>
            <p className="text-gray-600 text-lg leading-relaxed max-w-3xl mx-auto">
              {t("faq.subtitle")}
            </p>
          </div>

          {/* FAQ Accordion */}
          <div className="space-y-6 mb-16">
            {questions.map((faq, index) => (
              <div
                key={index}
                className={`border rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 bg-white overflow-hidden ${
                  activeIndex === index ? categoryColors[index] : "border-gray-200"
                }`}
              >
                <button
                  className="w-full text-left p-6 flex justify-between items-center focus:outline-none hover:bg-gray-50/50 transition-colors group"
                  onClick={() => setActiveIndex((prev) => (prev === index ? null : index))}
                  aria-expanded={activeIndex === index}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className="flex-shrink-0">{categoryIcons[index]}</div>
                    <span className="text-lg font-semibold text-gray-800 group-hover:text-gray-900 transition-colors">
                      {faq.q}
                    </span>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {activeIndex === index ? (
                      <FaChevronUp className="text-[#009fe3] text-lg" />
                    ) : (
                      <FaChevronDown className="text-gray-400 group-hover:text-[#009fe3] text-lg transition-colors" />
                    )}
                  </div>
                </button>

                <div
                  className={`transition-all duration-300 ease-in-out ${
                    activeIndex === index ? "max-h-96 opacity-100" : "max-h-0 opacity-0 overflow-hidden"
                  }`}
                >
                  <div className="px-6 pb-6">
                    <div className="pt-2 border-t border-gray-100">
                      <p className="text-gray-700 text-base leading-relaxed mt-4">{faq.a}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12 border border-gray-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#009fe3]/10 to-cyan-500/10 rounded-full -translate-y-16 translate-x-16"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-cyan-500/10 to-[#009fe3]/10 rounded-full translate-y-12 -translate-x-12"></div>

            <div className="text-center relative z-10">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-[#009fe3] to-cyan-500 rounded-xl mb-6 shadow-lg">
                <FaEnvelope className="text-2xl text-white" />
              </div>
              <h4 className="text-3xl font-bold mb-4 text-gray-900">{t("faq.stillHaveQuestions")}</h4>
              <p className="mb-8 text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto">
                {t("faq.stillHaveQuestionsDesc")}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to={route.contactUs}
                  className="inline-flex items-center gap-2 bg-gradient-to-r from-[#009fe3] to-cyan-500 hover:shadow-lg text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-200 transform hover:-translate-y-0.5"
                >
                  <FaEnvelope className="text-sm" />
                  {t("faq.contactUs")}
                </Link>

                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>or</span>
                  <div className="text-center">
                    <p className="font-semibold text-gray-700">{t("faq.callUs")}</p>
                    <p className="text-[#009fe3] font-bold">+880 186 755 5287</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default FaqG2G;
